import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Modal, Tab, Tabs, Typography, Select, MenuItem, Button, Card, Grid, LinearProgress, Link,
  FormControl,
  InputLabel,
  IconButton, Skeleton, Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow, TablePagination, ToggleButton,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import DateRangePicker from '../../components/DatePicker';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import 'jspdf-autotable';
import CircularProgress from '@mui/material/CircularProgress';
import http from "../../utils/http-common";
import { toast } from "react-toastify";
import { styled } from '@mui/material/styles';
import { FileUpload as ImportIcon, Close as CloseIcon, Share as ShareIcon, Add as AddIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    padding: '8px 16px',
    fontSize: '16px',
    fontWeight: 500,
    color: '#333',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: 'none',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none'
  }
}));
const LoadingCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#fff',
  height: '100%',
  minHeight: '40px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));
const ActionButton = styled(Button)(({ theme }) => ({
  color: 'black',
  backgroundColor: 'transparent',
  border: 'none',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'uppercase',
  padding: '4px 8px',
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 0.8
  },
  '& .MuiButton-startIcon': {
    marginRight: 8
  }
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 500,
  color: '#666',
  minWidth: 80,
  padding: '6px 12px',  // Added reduced padding
  minHeight: '35px'
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    height: '2px',
    borderRadius: '2px'
  },
  minHeight: '35px',  // Added reduced height
  '& .MuiTabs-flexContainer': {
    minHeight: '35px'  // Added reduced height
  }
}));

const MetricCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(0.2),
  paddingLeft: theme.spacing(1.5),
  paddingTop: theme.spacing(0.5),
  borderRadius: theme.spacing(1.5),
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#E5EEFB66',
  height: '100%',
  minHeight: '40px',  // Reduced from 50px
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),  // Reduced from 0.5
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '24px',  // Reduced from 28px
  fontWeight: 500,
  color: '#333',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.25),  // Reduced from 0.5
  lineHeight: 1
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',  // Reduced from 13px
  color: '#666',
  fontWeight: 500,
  marginBottom: theme.spacing(0.25)  // Reduced from 0.5
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  backgroundColor: '#E0E0E0',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#6200EE'
  }
}));

const StatusChip = styled(Box)(({ color }) => ({
  padding: '6px 12px 3px 12px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 500,
  backgroundColor: color,
  color: '#fff',
  display: 'inline-block',
  alignItems: 'center',
  height: '20px',
  width: '60px',  // Added fixed width
  textAlign: 'center',  // Center the text
  whiteSpace: 'nowrap',  // Prevent text wrapping
  overflow: 'hidden',  // Hide overflow text
  textOverflow: 'ellipsis'
}));

const ShareModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ShareModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '8px',
  padding: '24px',
  width: '640px',
  maxWidth: '90vw',
  maxHeight: '98vh',
  overflowY: 'visible',
}));

const InfoBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#FFF6E5',
  padding: '8px 12px',
  borderRadius: '8px',
  marginTop: '8px',
  marginBottom: '8px',
}));

export default function PlanDashboard() {
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedVersion, setSelectedVersion] = useState('');
  const [dashboardDataByVersion, setDashboardDataByVersion] = useState({});
  const [versionList, setVersionList] = useState([]);
  const [currentDashboardData, setCurrentDashboardData] = useState(null);
  const [planData, setPlanData] = useState([]);
  const [planName, setPlanName] = useState('');
  const userID = localStorage.getItem("user_id");
  const [planId, setPlanId] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
const [baseDashboardData, setBaseDashboardData] = useState(null);
  const totalApiCalls = 7; // Total number of API calls we make
  let completedApiCalls = 0;

  // Share modal states
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [emailOptions, setEmailOptions] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [subject, setSubject] = useState(`You've Been Invited to Join a Plan Dashboard | PlanShare.ai`);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({ subject: '', message: '', email: '' });
  const [accessType, setAccessType] = useState('limited-access');
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [isCopied, setIsCopied] = useState(false);

  const updateLoadingProgress = () => {
    completedApiCalls++;
    setLoadingProgress((completedApiCalls / totalApiCalls) * 100);
    if (completedApiCalls === totalApiCalls) {
      setIsLoading(false);
      completedApiCalls = 0;
    }
  };

  // Share modal handlers
  const handleShareModalOpen = () => {
    setShareModalOpen(true);
    getUsers();
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
    setSelectedEmails([]);
    setErrors({ subject: '', message: '', email: '' });
    setAccessType('limited-access');
    setIsCopied(false);
  };

  // Check if current view is from a shared link
  const isSharedView = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('id');
  };
  const planIdData = localStorage.getItem("planId");
 const shareLink = `${window.location.origin}/home/planDashboard?id=${planIdData}&version=${selectedVersion}`;
 const handleGenerateLink = async () => {
    try {
      
      const response = await http.post("/master_service/full_access/access_link/update", {
        plan_id: planId,
        from_date: dateRange.startDate?.toISOString().split('T')[0],
        end_date: dateRange.endDate?.toISOString().split('T')[0],
        plan_link: shareLink
      });
      if (response.status === 200) {
              toast.success("Link has been successfully copied");
            }
    } catch (error) {
      console.error("Error generating share link:", error);
      toast.error("Failed to generate share link");
    }
  };

  const handleCopyLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink)
        .then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), 2000);
        })
        .catch(() => {
          toast.error("Failed to copy link");
        });
    }
  };

  const getUsers = async () => {
    try {
      if (!planId) {
        console.warn("planId is missing, skipping API call.");
        return;
      }

      const response = await http.post("/master_service/limited/access/drop/down", {
        plan_id: planId
      });
      if (!Array.isArray(response.data)) {
        console.error("Unexpected response format:", response.data);
        return;
      }
      const emailList = response.data
        .filter((role) => role.email)
        .map((role) => ({
          label: role.email,
          value: role.email,
        }));
      setEmailOptions(emailList);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleSendInvite = async () => {
    const newErrors = {};

    if (!selectedEmails || selectedEmails.length === 0) {
      newErrors.email = "Please enter at least one email address";
    }

    if (!subject || subject.trim() === "") {
      newErrors.subject = "Subject is required";
    }

    if (!message || message.trim() === "") {
      newErrors.message = "Message is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const orgUserId = localStorage.getItem("user_id");
      const currentDate = new Date();
      const accessSendDate = currentDate.toISOString().split("T")[0];
      const accessSendTime = currentDate.toTimeString().split(" ")[0];
      
      const formData = new FormData();
      formData.append('org_user_id', orgUserId);
      formData.append('plan_id', planId);
      formData.append('mail_id', selectedEmails.map(e => e.value));
      formData.append('access_send_date', accessSendDate);
      formData.append('access_send_time', accessSendTime);
      formData.append('status', "Active");
      formData.append('access_type', "limited_access");
      formData.append('content', message);
      formData.append('subject', subject);

      // Add version information if a version is selected
      if (selectedVersion) {
        formData.append('version', selectedVersion);
      } else {
        // If no version is selected, send empty string to indicate latest version
        formData.append('version', '');
      }

      // // Add version data if available
      // if (currentDashboardData) {
      //   formData.append('version_data', JSON.stringify(currentDashboardData));
      // }

      const response = await http.post("/master_service/limited/access", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 201) {
        toast.success("Invite sent successfully!");
        handleShareModalClose();
      }
    } catch (error) {
      console.error("Error sending invite:", error);
      toast.error("Failed to send invite");
    }
  };

useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const urlPlanId = urlParams.get('id');
  const urlVersion = urlParams.get('version'); // may be '' for latest

  if (urlPlanId) {
    setPlanId(urlPlanId);

    const fetchSharedPlanData = async () => {
      try {
        const response = await http.get(`/master_service/get_plan/${urlPlanId}`);
        if (response.data) {
          const plan = response.data;
          setPlanName(plan.plan_name);
          setPlanData([
            {
              plan_id: plan.plan_id,
              label: plan.plan_name,
              value: plan.plan_id,
            },
          ]);

          let dashRes;
          let resArray = [];

          try {
            dashRes = await http.post("/master_service/get_dashboard/data", { plan_id: plan.plan_id });
            resArray = dashRes.data?.data || [];
          } catch (err) {
            // 🟥 Handle 404 or network error gracefully
            if (err.response && err.response.status === 404) {
              console.warn("Dashboard data not found (404) — falling back to plan-level data");
            } else {
              console.error("Error fetching dashboard data:", err);
            }

            // Fallback logic: no version data, fetch base details
            setVersionList([]);
            setDashboardDataByVersion({});
            setSelectedVersion('');
            setCurrentDashboardData(null);

            await Promise.all([
              fetchJsonData(plan.plan_id),
              fetchIssueData(plan.plan_id),
              fetchRiskData(plan.plan_id),
              fetchDependencyData(plan.plan_id),
              fetchConstraintData(plan.plan_id),
              fetchAssumptionData(plan.plan_id),
              fetchDetails(plan.plan_id),
            ]);

            return; // stop here
          }

          // 🟢 Proceed if version-based data exists
          if (!Array.isArray(resArray) || resArray.length === 0) {
            // No version-based data → fallback
            setVersionList([]);
            setDashboardDataByVersion({});
            setSelectedVersion('');
            setCurrentDashboardData(null);

            await Promise.all([
              fetchJsonData(plan.plan_id),
              fetchIssueData(plan.plan_id),
              fetchRiskData(plan.plan_id),
              fetchDependencyData(plan.plan_id),
              fetchConstraintData(plan.plan_id),
              fetchAssumptionData(plan.plan_id),
              fetchDetails(plan.plan_id),
            ]);

            return;
          }

          // 🟢 Build version list
          const versionMap = {};
          const versions = [];

          resArray.forEach((item, idx) => {
            const version = item.version || item.headerData?.version;
            const createdAt =
              item.headerData?.created_At ||
              item.headerData?.createdAt ||
              item.headerData?.created_at ||
              null;
            if (version) {
              versionMap[version] = item;
              versions.push({ version, createdAt });
            }
            if (idx === 0) versionMap["default"] = item;
          });

          versions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setDashboardDataByVersion(versionMap);
          setVersionList(versions);

          const base = resArray[0];
          const headerData = base.headerData || {};
          setCurrentDashboardData(base);
          setOverallProgress(parseFloat(headerData.overall_complete) || 0);
          setLateTaskCount(headerData.late_task || 0);
          setOpenIssueCount(headerData.open_issues || 0);
          setTopRiskScore(parseFloat(headerData.top_risk_exposure) || 0);

          // 🟡 Handle version selection logic
          setSelectedVersion(urlVersion ? urlVersion : '');
          if (urlVersion && versionMap[urlVersion]) {
            const sel = versionMap[urlVersion];
            const h = sel.headerData || {};
            setCurrentDashboardData(sel);
            setOverallProgress(parseFloat(h.overall_complete) || 0);
            setLateTaskCount(h.late_task || 0);
            setOpenIssueCount(h.open_issues || 0);
            setTopRiskScore(parseFloat(h.top_risk_exposure) || 0);
          } else if (urlVersion && !versionMap[urlVersion]) {
            // Requested version not found → fallback to plan-level data
            setSelectedVersion('');
            setCurrentDashboardData(null);

            await Promise.all([
              fetchJsonData(plan.plan_id),
              fetchIssueData(plan.plan_id),
              fetchRiskData(plan.plan_id),
              fetchDependencyData(plan.plan_id),
              fetchConstraintData(plan.plan_id),
              fetchAssumptionData(plan.plan_id),
              fetchDetails(plan.plan_id),
            ]);
          }
        }
      } catch (err) {
        console.error("Error loading shared plan data:", err);
      }
    };

    fetchSharedPlanData();
  } else {
    getPlan();
  }
}, []);

const handleExportPDF = async () => {
  try {
    const hiddenElements = document.querySelectorAll('.no-print');
    hiddenElements.forEach(el => el.style.display = 'none');
    const element = document.getElementById("dashboard-container"); // main container to export
    if (!element) {
      toast.error("Dashboard not found for export!");
      return;
    }

    const canvas = await html2canvas(element, {
      scale: 2, // high resolution
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    hiddenElements.forEach(el => el.style.display = '');
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    if (pdfHeight > 297) {
      let position = 0;
      let heightLeft = pdfHeight;
      while (heightLeft > 0) {
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= 297;
        if (heightLeft > 0) {
          pdf.addPage();
          position = 0;
        }
      }
    } else {
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    }

    pdf.save("PlanDashboard.pdf");
    toast.success("Dashboard exported successfully!");
  } catch (error) {
    console.error("Export failed:", error);
    toast.error("Failed to export PDF");
  }
};

  const [resources, setResources] = useState([]);
  const fetchDetails = async (planId) => {
    try {
      const response = await http.post("master_service/plan_role/user/mapping/details", { plan_id: planId });
      if (response.data?.mappings?.length > 0) {
        const fetchedResources = response.data.mappings
          .filter(item => item.user?.email) // ✅ Only include records with email
          .map(item => ({
            label: item.user?.user_name || "",
            value: item.user?.email, // ✅ Use email as value
          }));
        setResources(fetchedResources);
        if (!issueData.assignedTo && fetchedResources.length > 0) {
          setIssueData(prev => ({ ...prev, assignedTo: fetchedResources[0].value }));
        }
      }
    } catch (error) {
      console.error("Error fetching resources:", error);
      if (error.response?.status === 404) {
        console.error("Error 404 : No resources found for this plan.");
        setResources([]);
      }
    }
  };
const fetchDashboardData = async (planId) => {
  try {
    const payload = { plan_id: planId };
    const response = await http.post("/master_service/get_dashboard/data", payload);
    const resArray = response.data.data;

    if (!Array.isArray(resArray) || resArray.length === 0) {
      console.warn("No dashboard data found for this plan");
      setVersionList([]);
      setDashboardDataByVersion({});
      return;
    }

    const versionMap = {};
    const versions = [];

    resArray.forEach((item, idx) => {
      const version = item.version || item.headerData?.version;
      const createdAt =
        item.headerData?.created_At ||
        item.headerData?.createdAt ||
        item.headerData?.created_at ||
        null;

      if (version) {
        versionMap[version] = item; // ✅ link version → data
        versions.push({ version, createdAt });
      }

      // First item as default/base data
      if (idx === 0) versionMap["default"] = item;
    });

    // Sort versions (latest first)
    versions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    // ✅ Save both lists
    setDashboardDataByVersion(versionMap);
    setVersionList(versions);
    setBaseDashboardData(resArray[0]);

    // Default: show base data
    const headerData = resArray[0].headerData || {};
    setCurrentDashboardData(resArray[0]);
    setOverallProgress(parseFloat(headerData.overall_complete) || 0);
    setLateTaskCount(headerData.late_task || 0);
    setOpenIssueCount(headerData.open_issues || 0);
    setTopRiskScore(parseFloat(headerData.top_risk_exposure) || 0);

  } catch (error) {
    console.error("Error fetching dashboard data:", error);
  }
};



  const [issues, setIssues] = useState([]);
  const [openIssueCount, setOpenIssueCount] = useState(0);
  const [assignedUserCount, setAssignedUserCount] = useState(0);
  const fetchIssueData = async (planId) => {
    try {
      const response = await http.get(`/master_service/get_project/plan/summary/${planId}`);
      if (response.status === 200) {
        const data = response.data.issues.map(item => ({
          id: item.issue_id,
          issue_type: item.issue_type,
          who_raised_it: item.who_raised_it,
          description: item.description,
          priority: item.priority,
          assignedTo: item.assigned_to,
          targetResolutionDate: item.target_resolution_date,
          finalSolution: item.final_solution,
          status: item.status,
          created_At: item.created_At,
          updated_at: item.updated_at,
        }));
        const activeIssues = data.filter(issue => 
        issue.status && issue.status.toLowerCase() !== "completed"
      );
        const uniqueAssignedToCount = new Set(
        activeIssues
          .map(issue => issue.assignedTo)
          .filter(value => value && value.trim() !== "")
      ).size;
      setAssignedUserCount(uniqueAssignedToCount);
        const priorityOrder = { High: 1, Medium: 2, Low: 3 };
        // --- Sort by priority (High > Medium > Low) and within same priority by oldest date ---
        const sorted = activeIssues.sort((a, b) => {
          const priorityDiff = (priorityOrder[a.priority] || 4) - (priorityOrder[b.priority] || 4);
          if (priorityDiff !== 0) return priorityDiff;

          // If same priority → compare by oldest date
          const dateA = new Date(a.targetResolutionDate || a.created_At);
          const dateB = new Date(b.targetResolutionDate || b.created_At);
          return dateA - dateB; // older date first
        });

        const top3 = sorted.slice(0, 3);
        setIssues(top3);
        setOpenIssueCount(activeIssues.length);
      }
    } catch (error) {
      console.error("Error fetching user data", error);
    }
  };
  const [risks, setRisks] = useState([]);
  const [topRiskScore, setTopRiskScore] = useState(0);
  const [uniqueRiskOwnerCount, setUniqueRiskOwnerCount] = useState(0);
  const fetchRiskData = async (planId) => {
    try {
      const response = await http.get(`/master_service/get_project/plan/summary/${planId}`);
      if (response.status === 200) {
        const data = response.data.risks.map((item) => {
          // Use revised_risk_score if available, else fallback to risk_score
          const score = parseFloat(item.revised_risk_score) || parseFloat(item.risk_score) || 0;
          return {
            id: item.risk_id,
            risk_statement: item.risk_statement,
            risk_owner: item.risk_owner,
            probability: item.probability,
            impact: item.impact,
            risk_score: score,
            response_strategies: item.response_strategies,
            action: item.action,
            status: item.status,
            comment: item.comment,
            created_At: item.created_At,
            updated_at: item.updated_at,
          };
        });
        const activeRisks = data.filter(risk => 
        risk.status && !['completed', 'closed'].includes(risk.status.toLowerCase())
      );
        const uniqueRiskOwnerCount = new Set(
        activeRisks
          .map(risk => risk.risk_owner)
          .filter(value => value && value.trim() !== "")
      ).size;
      setUniqueRiskOwnerCount(uniqueRiskOwnerCount);
        // Sort descending by effective risk_score (considering revised)
        const sorted = activeRisks.sort((a, b) => b.risk_score - a.risk_score);

        // Keep top 3 risks
        const top3 = sorted.slice(0, 3);
        setRisks(top3);

        // Take highest score for display
        const highestScore = top3.length > 0 ? top3[0].risk_score : 0;
        setTopRiskScore(highestScore);
      }
    } catch (error) {
      console.error("Error fetching user data", error);
    }
  };
  const [dependencies, setDependencies] = useState([]);
  const fetchDependencyData = async (planId) => {
    try {
      const response = await http.get(`/master_service/get_project/plan/summary/${planId}`);
      if (response.status === 200) {
        const data = response.data.dependencies.map(item => ({
          id: item.dept_id,
          name: item.name,
          from: item.from,
          owner: item.owner,
          due_date: item.due_date,
          status: item.status,
          created_At: item.created_At,
          updated_at: item.updated_at,
        }));
        const statusOrder = { High: 1, Medium: 2, Low: 3 };
        const sorted = data.sort((a, b) => (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4));
        const top3 = sorted.slice(0, 3);
        setDependencies(top3);
      }
    } catch (error) {
      console.error("Error fetching user data", error);
    }
  };
  const [constraints, setConstraints] = useState([]);
  const fetchConstraintData = async (planId) => {
    try {
      const response = await http.get(`/master_service/get_project/plan/summary/${planId}`);
      if (response.status === 200) {
        const data = response.data.constraints.map(item => ({
          id: item.const_id,
          name: item.name,
          status: item.status,
          created_At: item.created_At,
          updated_at: item.updated_at,
        }));
        const statusOrder = { High: 1, Medium: 2, Low: 3 };
        const sorted = data.sort((a, b) => (statusOrder[a.status] || 4) - (statusOrder[b.status] || 4));
        const top3 = sorted.slice(0, 3);
        setConstraints(top3);
      }
    } catch (error) {
      console.error("Error fetching user data", error);
    }
  };
  const [assumptions, setAssumptions] = useState([]);
  const fetchAssumptionData = async (planId) => {
    try {
      const response = await http.get(`/master_service/get_project/plan/summary/${planId}`);
      if (response.status === 200) {
        const data = response.data.assumptions.map(item => ({
          id: item.ass_id,
          category: item.category,
          dateLogged: item.date_logged,
          assumption: item.assumption,
          confidence: item.confidence,
          owner: item.owner,
          rationale: item.rationale,
          impact: item.impact,
          status: item.status,
          created_At: item.created_At,
          updated_at: item.updated_at,
        }));
        setAssumptions(data);
      }
    } catch (error) {
      console.error("Error fetching user data", error);
    }
  };
  const getPlan = async () => {
    try {
      const response = await http.get(
        "/master_service/get_all/plan_master/records"
      );

      if (response.status === 200) {
        const sortedData = response.data
          .filter((item) => item.user_id === userID)
          .sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
          .map((item) => ({
            id: item.id,
            plan_name: item.plan_name,
            plan_id: item.plan_id,
            label: item.plan_name,
            value: item.plan_id,
          }));
        setPlanData(sortedData);
        
      }
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };
  const [jsonData, setJsonData] = useState([]);
  const [resourceSummary, setResourceSummary] = useState([]);
  const [overallProgress, setOverallProgress] = useState(0);
  const [topSummaryTasks, setTopSummaryTasks] = useState([]);
  const [lateTasks, setLateTasks] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [costData, setCostData] = useState({baseline: 0, actual: 0});
  const [lateTaskCount, setLateTaskCount] = useState(0);
  const [criticalTaskCount, setCriticalTaskCount] = useState(0);
  const [milestoneTasks, setMilestoneTasks] = useState([]);
  useEffect(() => {
    if (currentDashboardData) {
      const { headerData, dashboardDetailsData } = currentDashboardData;
      
      // Update header metrics
      setOverallProgress(parseFloat(headerData.overall_complete) || 0);
      setLateTaskCount(headerData.late_task || 0);
      setOpenIssueCount(headerData.open_issues || 0);
      setTopRiskScore(parseFloat(headerData.top_risk_exposure) || 0);

      // Update task completion by category
      if (dashboardDetailsData?.complete_by_task) {
        const summaryTasks = Object.entries(dashboardDetailsData.complete_by_task).map(([name, progress]) => ({
          name,
          progress: parseFloat(progress) || 0
        }));
        setTopSummaryTasks(summaryTasks);
      }

      // Update dependencies
      if (dashboardDetailsData?.dependencies) {
        setDependencies(dashboardDetailsData.dependencies.map(dep => ({
          id: Math.random().toString(), // Temporary ID for rendering
          name: dep.title,
          from: dep.source,
          owner: dep.owner,
          due_date: dep.due_date,
          status: dep.status
        })));
      }

      // Update issues
      if (dashboardDetailsData?.issues) {
        setIssues(dashboardDetailsData.issues.map(issue => ({
          id: Math.random().toString(),
          description: issue.title,
          who_raised_it: issue.owner,
          priority: issue.priority,
          targetResolutionDate: issue.due_date,
          status: 'Open'
        })));
      }

      // Update risks
      if (dashboardDetailsData?.risk) {
        setRisks(dashboardDetailsData.risk.map(risk => ({
          id: Math.random().toString(),
          risk_statement: risk.title,
          risk_owner: risk.owner,
          risk_score: parseFloat(risk.exposure.replace('Exp ', '')) || 0,
          status: risk.category
        })));
      }

      // Update constraints
      if (dashboardDetailsData?.constraints) {
        setConstraints(dashboardDetailsData.constraints.map(constraint => ({
          id: Math.random().toString(),
          name: constraint.title,
          status: constraint.category
        })));
      }
    }
  }, [currentDashboardData]);
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(10);
const [page1, setPage1] = useState(0);
const [rowsPerPage1, setRowsPerPage1] = useState(10);

const handleChangePage = (event, newPage) => {
  setPage(newPage);
};

const handleChangePage1 = (event, newPage) => {
  setPage1(newPage);
};

const handleChangeRowsPerPage = (event) => {
  setRowsPerPage(parseInt(event.target.value, 10));
  setPage(0);
};
const handleChangeRowsPerPage1 = (event) => {
  setRowsPerPage1(parseInt(event.target.value, 10));
  setPage1(0);
};
  const fetchJsonData = async (planId) => {
  try {
    const response = await http.post(`/master_service/store-plan-data`, {
      plan_id: planId,
    });

    if (response.status === 200 || response.status === 201) {
      const tasks = response.data?.data?.data?.tasks || [];
      setJsonData(tasks);
       const totalCost = tasks[0].cost;
       const totalActualCost = tasks[0].ActualCost;
      setCostData({baseline: totalCost, actual: totalActualCost});
      const rootTask = tasks.find((task) => !task.parent);
      if (rootTask) setOverallProgress(rootTask.progress);

      const now = new Date();
      const lateTasks = tasks.filter((task) => task.end_date && new Date(task.end_date) < now);
      const upcomingTasks = tasks.filter((task) => task.start_date && new Date(task.start_date) > now);
      // ✅ Critical tasks only inside late tasks
      const criticalTasks = lateTasks.filter((task) => task.Critical === "True").length;

// --- Resource Assignment Summary ---
const resourceCountMap = {};

tasks.forEach((task) => {
  if (task.ResourceAssignments && typeof task.ResourceAssignments === "string") {
    // Match all resource=Something patterns
    const matches = task.ResourceAssignments.match(/resource=([^ ]+)/g);
    if (matches) {
      matches.forEach((match) => {
        const resource = match.replace("resource=", "").trim();
        if (!resourceCountMap[resource]) {
          resourceCountMap[resource] = 0;
        }
        resourceCountMap[resource] += 1;
      });
    }
  }
});

// Convert to array format
const resourceSummary = Object.entries(resourceCountMap).map(([resource, count]) => ({
  resource,
  count,
}));
setResourceSummary(resourceSummary);
      // --- Get all Summary Tasks (no slicing to top 3) ---
      const summaryMap = {};

      tasks.forEach((task) => {
        const progress = parseFloat(task.progress || 0);
        let parentName = "";

        if (task.parent && typeof task.parent === "string") {
          const match = task.parent.match(/name=([^,\]]+)/);
          if (match && match[1]) parentName = match[1].trim();
        }

        if (parentName) {
          if (!summaryMap[parentName]) {
            summaryMap[parentName] = { totalProgress: 0, count: 0 };
          }
          summaryMap[parentName].totalProgress += progress;
          summaryMap[parentName].count += 1;
        }
      });

      const allSummaries = Object.entries(summaryMap).map(([name, data]) => ({
        name,
        progress: parseFloat((data.totalProgress / data.count).toFixed(1)),
      }));

      // ✅ Set all summary tasks (sorted if required)
      const sortedSummaries = allSummaries.sort((a, b) => b.progress - a.progress);
      setTopSummaryTasks(sortedSummaries); // <-- No slicing now (has all values)

      // --- Milestones (unchanged) ---
      const milestoneList = tasks
        .filter((task) => task.Milestone === "True")
        .map((task) => {
          let owner = "";
          if (task.ResourceAssignments && typeof task.ResourceAssignments === "string") {
            const match = task.ResourceAssignments.match(/resource=([^ ]+)/);
            if (match && match[1]) owner = match[1].trim();
          }
const actualDate = task.end_date ? new Date(task.end_date) : null;
    const plannedDate = task.BaselineFinish ? new Date(task.BaselineFinish) : null;
          return {
            name: task.text || "Untitled",
            owner,
            planned: plannedDate,
            actual: actualDate,
            isOverdue: actualDate && actualDate < now,
      nextDate: actualDate || plannedDate || new Date(8640000000000000),
          };
        });
        const sortedMilestones = milestoneList.sort((a, b) => {
  if (a.isOverdue && b.isOverdue) {
    return b.actual - a.actual; // Most recently overdue first
  }
  if (a.isOverdue) return -1;
  if (b.isOverdue) return 1;
  return a.nextDate - b.nextDate; // Upcoming sorted by nearest
});

// ✅ Take top 3
setMilestoneTasks(sortedMilestones.slice(0, 3).map(m => ({
  name: m.name,
  owner: m.owner,
  planned: m.planned ? m.planned.toISOString().split("T")[0] : "",
  actual: m.actual ? m.actual.toISOString().split("T")[0] : "",
  variance: 0,
})));
      setLateTasks(lateTasks);
      setUpcomingTasks(upcomingTasks);
      setLateTaskCount(lateTasks.length);
      setCriticalTaskCount(criticalTasks);
    }
  } catch (error) {
    console.error("Not found for this plan.");
  }
};



  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handlePlanChange = async (event) => {
    const selectedPlanId = event.target.value;
    setIsEditing(false);
    const selectedPlanObj = planData.find(
      (plan) => plan.plan_id === selectedPlanId
    );

    // ✅ Store the numeric `id` in localStorage as planId
    if (selectedPlanObj) {
      localStorage.setItem("planId", selectedPlanObj.id);
    }
    setSelectedPlan(selectedPlanId);
    
  // ✅ Reset version state when plan changes
  setSelectedVersion("");        // Clear selected version
  setVersionList([]);            // Clear version list
  setCurrentDashboardData(null); // Clear dashboard data

  // ✅ Reset metric values also (if any)
  setOverallProgress(0);
  setLateTaskCount(0);
  setOpenIssueCount(0);
  setTopRiskScore(0);
    setIsLoading(true);
    setLoadingProgress(0);
    completedApiCalls = 0;

    try {
      // Execute all API calls in parallel
      await Promise.all([
         fetchJsonData(selectedPlanId).finally(updateLoadingProgress),
        fetchIssueData(selectedPlanId).finally(updateLoadingProgress),
        fetchRiskData(selectedPlanId).finally(updateLoadingProgress),
        fetchDependencyData(selectedPlanId).finally(updateLoadingProgress),
        fetchConstraintData(selectedPlanId).finally(updateLoadingProgress),
        fetchAssumptionData(selectedPlanId).finally(updateLoadingProgress),
        fetchDashboardData(selectedPlanId).finally(updateLoadingProgress),
        fetchDetails(selectedPlanId).finally(updateLoadingProgress)
      ]);
      setPlanId(selectedPlanId);
    } catch (error) {
      console.error("Error loading plan data:", error);
      toast.error("Error loading plan data. Please try again.");
    } finally {
      if (completedApiCalls < totalApiCalls) {
        setIsLoading(false);
        completedApiCalls = 0;
      }
    }
  };
const renderLoadingSkeleton = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Grid container spacing={2}>
        {[...Array(4)].map((_, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <LoadingCard>
              <Skeleton variant="text" width="60%" height={10} />
              <Skeleton variant="text" width="40%" height={10} />
              {index === 1 && <Skeleton variant="text" width="70%" height={10} />}
            </LoadingCard>
          </Grid>
        ))}
      </Grid>
      
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {[...Array(3)].map((_, index) => (
          <Grid item xs={12} md={4} key={`section-${index}`}>
            <LoadingCard>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Skeleton variant="text" width="40%" height={15} />
                <Skeleton variant="text" width="20%" height={15} />
              </Box>
              {[...Array(3)].map((_, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Skeleton variant="text" width="70%" height={15} />
                  <Skeleton variant="rectangular" width={60} height={15} sx={{ borderRadius: 1 }} />
                </Box>
              ))}
            </LoadingCard>
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {[...Array(3)].map((_, index) => (
          <Grid item xs={12} md={4} key={`section-${index}`}>
            <LoadingCard>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Skeleton variant="text" width="40%" height={15} />
                <Skeleton variant="text" width="20%" height={15} />
              </Box>
              {[...Array(3)].map((_, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Skeleton variant="text" width="70%" height={15} />
                  <Skeleton variant="rectangular" width={60} height={15} sx={{ borderRadius: 1 }} />
                </Box>
              ))}
            </LoadingCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const handleVersionChange = (event) => {
    const selected = event.target.value;
    setSelectedVersion(selected);

    // If no version-data exists (plan-only share fallback), keep current task-derived metrics
    if (!dashboardDataByVersion || Object.keys(dashboardDataByVersion).length === 0) {
      return;
    }

    // Reset to default if user selects "Select Version"
    if (!selected) {
      const defaultData = dashboardDataByVersion["default"] || baseDashboardData;
      if (defaultData) {
        const headerData = defaultData.headerData || {};
        setCurrentDashboardData(defaultData);
        setOverallProgress(parseFloat(headerData.overall_complete) || 0);
        setLateTaskCount(headerData.late_task || 0);
        setOpenIssueCount(headerData.open_issues || 0);
        setTopRiskScore(parseFloat(headerData.top_risk_exposure) || 0);
      }
      return;
    }

    // Handle version-specific selection
    const selectedData = dashboardDataByVersion[selected];
    if (selectedData) {
      const headerData = selectedData.headerData || {};
      setCurrentDashboardData(selectedData);
      setOverallProgress(parseFloat(headerData.overall_complete) || 0);
      setLateTaskCount(headerData.late_task || 0);
      setOpenIssueCount(headerData.open_issues || 0);
      setTopRiskScore(parseFloat(headerData.top_risk_exposure) || 0);
    } else {
      console.warn("No data found for version:", selected);
    }
  };




  const [customColor, setCustomColor] = useState(null);
  const renderProjectOverview = () => (
    isLoading ? renderLoadingSkeleton() : (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
  <MetricCard
    sx={{
      //backgroundColor: "#1e1e1e", // fixed card color
      color: "#fff",
      transition: "background-color 0.3s ease",
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <MetricLabel>Overall % Complete</MetricLabel>
    </Box>

    {/* --- Color Change Button + Indicator --- */}
    <Box display="flex" justifyContent="center" alignItems="center" mt={1} gap={1}>
      {/* Circle Indicator */}
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "50%",
          backgroundColor:
            customColor === "#f44336"
              ? "#f44336"
              : customColor === "#ffb300"
              ? "#ffb300"
              : customColor === "#4caf50"
              ? "#ffb300"
              : "#4caf50", // gray for Auto
          border: "1px solid #555",
          boxShadow: `0 0 6px ${customColor || "#9e9e9e"}`,
          transition: "background-color 0.3s ease, box-shadow 0.3s ease",
        }}
      />

      {/* Change Color Button */}
      <Button
    variant="outlined"
    onClick={() => {
      if (customColor === "#f44336") setCustomColor("#ffb300"); // Red → Amber
      else if (customColor === "#ffb300") setCustomColor("#4caf50"); // Amber → Green
      else if (customColor === "#4caf50") setCustomColor("#4caf50"); // Green → Auto
      else setCustomColor("#f44336"); // Start from Red
    }}
    size="small"
    disabled={!isEditing}
    sx={{
      color: isEditing ? "#333" : "#999",
      borderColor: isEditing ? "#333" : "#999",
      textTransform: "none",
      borderRadius: "8px",
      fontSize: "11px",
      fontWeight: 500,
      opacity: isEditing ? 1 : 0.6,
      cursor: isEditing ? "pointer" : "not-allowed",
      "&:hover": {
        borderColor: isEditing ? "#000" : "#999",
        backgroundColor: isEditing ? "rgba(0,0,0,0.05)" : "transparent",
      },
    }}
  >
    Change Status
  </Button>
    </Box>

    {/* --- Circular Progress --- */}
    <Box mt={1} display="flex" justifyContent="center" alignItems="center">
      {isEditing ? (
        <TextField
          size="small"
          type="number"
          value={overallProgress}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*\.?\d*$/.test(value)) {
              setOverallProgress(value);
            }
          }}
          sx={{
            width: "70px",
            fontSize: "12px",
            background: "#fff",
            borderRadius: 1,
          }}
        />
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Box position="relative" display="inline-flex">
            {/* Background Circle */}
            <CircularProgress
              variant="determinate"
              value={100}
              style={{ color: "#e0e0e0", position: "absolute" }}
              size={60}
              thickness={5}
            />

            {/* Progress Circle */}
            <CircularProgress
              variant="determinate"
              value={Number(overallProgress)}
              style={{
                color:
                  Number(overallProgress) < 50
                    ? "#f44336"
                    : Number(overallProgress) < 80
                    ? "#ffb300"
                    : "#4caf50",
              }}
              size={60}
              thickness={5}
            />

            {/* Centered % Text */}
            <Box
              top={0}
              left={0}
              bottom={0}
              right={0}
              position="absolute"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography
                variant="caption"
                component="div"
                style={{ fontWeight: "bold", color: "#0e0d0dff"}}
              >
                {`${overallProgress}%`}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  </MetricCard>
</Grid>


        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricLabel>Late Tasks</MetricLabel>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'baseline', gap: 1 }}>
              {isEditing ? (
                <>
                  <TextField
                    size="small"
                    type="number"
                    value={lateTaskCount}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Allow only positive numbers (including decimals)
                      if (/^\d*\.?\d*$/.test(value)) {
                        setLateTaskCount(value);
                      }
                    }}
                    sx={{ width: "70px", fontSize: "12px" }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    value={criticalTaskCount}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Allow only positive numbers (including decimals)
                      if (/^\d*\.?\d*$/.test(value)) {
                        setCriticalTaskCount(value);
                      }
                    }}
                    sx={{ width: "120px", fontSize: "12px" }}
                    label="Critical Path"
                  />
                </>
              ) : (
                <>
                  <MetricValue>{lateTaskCount}</MetricValue>
                  <Typography sx={{ color: '#666', fontSize: '12px' }}>
                    Critical Path: {criticalTaskCount}
                  </Typography>
                </>
              )}
            </Box>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricLabel>Open Issues</MetricLabel>
            {isEditing ? (
              <TextField
                size="small"
                type="number"
                value={openIssueCount}
                onChange={(e) => {
                  const value = e.target.value;

                  // Allow only positive numbers (including decimals)
                  if (/^\d*\.?\d*$/.test(value)) {
                    setOpenIssueCount(value);
                  }
                }}
                sx={{ width: "70px", fontSize: "12px" }}
              />
            ) : (
              <MetricValue>{openIssueCount}</MetricValue>
            )}
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricLabel>Top Risk Exposure</MetricLabel>
            {isEditing ? (
              <TextField
                size="small"
                type="number"
                value={topRiskScore}
                onChange={(e) => {
                  const value = e.target.value;

                  // Allow only positive numbers (including decimals)
                  if (/^\d*\.?\d*$/.test(value)) {
                    setTopRiskScore(value);
                  }
                }}
                sx={{ width: "70px", fontSize: "12px" }}
              />
            ) : (
              <MetricValue>{topRiskScore}</MetricValue>
            )}
          </MetricCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: "0px" }}>
        <Grid item xs={12} md={4}>
          <MetricCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid #E0E0E0' }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, borderBottom: 'none !important' }}>Milestones</Typography>
              <Link className="no-print" component="button" onClick={() => navigate('/home/viewplan')} sx={{ color: '#6200EE', textDecoration: 'none', fontSize: '14px', fontFamily: "Inter, sans-serif", pr: 1}}>Manage</Link>
            </Box>
            <Box sx={{
              display: 'table',
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: 'Inter, San-Sarif',

            }}>
              <Box sx={{
                display: 'table-header-group',
                backgroundColor: '#F5F5F5',
                borderBottom: '1px solid #E0E0E0'
              }}>
                <Box sx={{ display: 'table-row' }}>
                  <Box sx={{ display: 'table-cell', py: 1, px: 0.75, fontSize: '11px', color: '#666', fontWeight: 500 }}>Name</Box>
                  <Box sx={{ display: 'table-cell', py: 1, px: 0.75, fontSize: '11px', color: '#666', fontWeight: 500 }}>Owner</Box>
                  <Box sx={{ display: 'table-cell', py: 1, px: 0.75, fontSize: '11px', color: '#666', fontWeight: 500 }}>Planned</Box>
                  <Box sx={{ display: 'table-cell', py: 1, px: 0.75, fontSize: '11px', color: '#666', fontWeight: 500 }}>Actual</Box>
                  <Box sx={{ display: 'table-cell', py: 1, px: 0.75, fontSize: '11px', color: '#666', fontWeight: 500 }}>Variance</Box>
                </Box>
              </Box>
              <Box sx={{ display: 'table-row-group' }}>
                {milestoneTasks.length > 0 ? (
                  milestoneTasks.map((m, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'table-row', '&:hover': { backgroundColor: '#F8F8F8' } }}
                    >
                      <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#333', borderBottom: '1px solid #E0E0E0' }}>
                        {m.name}
                      </Box>
                      <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#666', borderBottom: '1px solid #E0E0E0' }}>
                        {m.owner || "-"}
                      </Box>
                      <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#666', borderBottom: '1px solid #E0E0E0' }}>
                        {m.planned || "-"}
                      </Box>
                      <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#666', borderBottom: '1px solid #E0E0E0' }}>
                        {m.actual || "-"}
                      </Box>
                      <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#666', borderBottom: '1px solid #E0E0E0' }}>
                        {m.variance}
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ display: 'table-row' }}>
                    <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#666', borderBottom: '1px solid #E0E0E0' }}></Box>
                    <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#666', borderBottom: '1px solid #E0E0E0' }}></Box>
                    <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#666', borderBottom: '1px solid #E0E0E0' }}></Box>
                    <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#666', borderBottom: '1px solid #E0E0E0' }}></Box>
                    <Box sx={{ display: 'table-cell', py: 1.5, px: 0.75, fontSize: '11px', color: '#666', borderBottom: '1px solid #E0E0E0' }}></Box>
                  </Box>
                )}
              </Box>
            </Box>
          </MetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <MetricCard>
            <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>% Complete by Summary Tasks</Typography>
            </Box>
{/* Scrollable container */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxHeight: 200,         // Adjust height as needed
        overflowY: 'auto',      // Enables scrolling
        pr: 1,                  // Prevents scrollbar overlap with content
      }}
    >
      {topSummaryTasks.length > 0 ? (
        topSummaryTasks.map((task, index) => (
          <Box key={task.id}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.2 }}>
              <Typography sx={{ fontSize: "12px" }}>
                {index + 1}. {task.name}
              </Typography>
              {isEditing ? (
                <TextField
                  size="small"
                  type="number"
                  value={task.progress}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      const updated = [...topSummaryTasks];
                      updated[index].progress = value;
                      setTopSummaryTasks(updated);
                    }
                  }}
                  sx={{ width: "70px", fontSize: "12px" }}
                />
              ) : (
                <Typography sx={{ fontSize: "12px" }}>
                  {task.progress}%
                </Typography>
              )}
            </Box>
            <StyledLinearProgress variant="determinate" value={task.progress} />
          </Box>
        ))
      ) : (
        <Typography sx={{ fontSize: "12px", color: "#888" }}>
          No summary tasks found
        </Typography>
      )}
    </Box>
          </MetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <MetricCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, borderBottom: '1px solid #E0E0E0' }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>Dependencies (Receivables)</Typography>
              <Button className="no-print" size="small" startIcon={<AddIcon />} sx={{ color: '#6200EE', textTransform: 'none', minHeight: '28px', padding: '2px 8px' }} onClick={() => setDependencyModalOpen(true)}>Add</Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1, fontSize: '10px' }}>
              {dependencies.length > 0 ? (
                dependencies.map((dep) => (
                  <Box key={dep.id}>
                    <Typography sx={{ fontSize: "12px", mb: 0.5 }}>
                      {dep.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        color: "#666",
                        fontSize: "10px",
                      }}
                    >
                      <Typography sx={{ fontSize: "11px" }}>
                        From {dep.from} • Owner {dep.owner} • Due {dep.due_date}
                      </Typography>
                      <StatusChip color={
                        dep.status === "Pending" ? "#FFA726" :
                          dep.status === "Requested" ? "#666" :
                            dep.status === "Confirmed" ? "#66BB6A" : "#E0E0E0"
                      }>
                        {dep.status}
                      </StatusChip>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: "12px", color: "#999", mt: 1 }}>
                  No dependencies found.
                </Typography>
              )}
            </Box>
          </MetricCard>
        </Grid>
      </Grid>
      <Grid container spacing={2} mt={'0px'}>
        <Grid item xs={12} md={4}>
          <MetricCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid #E0E0E0' }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>Issues</Typography>
              <Button className="no-print" size="small" startIcon={<AddIcon />} sx={{ color: '#6200EE', textTransform: 'none', minHeight: '28px', padding: '2px 8px' }} onClick={() => setIssueModalOpen(true)}>Add</Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {issues.length > 0 ? (
                issues.map((issue) => (
                  <Box key={issue.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '12px', color: '#333' }}>
                        {issue.description || 'No description'}
                      </Typography>
                      <StatusChip
                        color={
                          issue.priority === 'High'
                            ? '#f44336'
                            : issue.priority === 'Medium'
                              ? '#ff9800'
                              : '#4caf50'
                        }
                      >
                        {issue.priority}
                      </StatusChip>
                    </Box>

                    <Typography sx={{ color: '#666', fontSize: '11px' }}>
                      Owner {issue.who_raised_it || 'N/A'} • {issue.issue_type || 'General'} • Due{' '}
                      {issue.targetResolutionDate
                        ? new Date(issue.targetResolutionDate).toISOString().split('T')[0]
                        : 'N/A'}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: '12px', color: '#999' }}>
                  No issues found for this plan
                </Typography>
              )}
            </Box>
          </MetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <MetricCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid #E0E0E0' }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>Risks (Top by Exposure)</Typography>
              <Button className="no-print" size='small' startIcon={<AddIcon />} sx={{ color: '#6200EE', textTransform: 'none', minHeight: '28px', padding: '2px 8px' }} onClick={() => setRiskModalOpen(true)}>Add</Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {risks.length > 0 ? (
                risks.map((risk) => (
                  <Box key={risk.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '12px', color: '#333' }}>
                        {risk.risk_statement || 'No risk statement'}
                      </Typography>

                      <StatusChip
                        sx={{
                          bgcolor:
                            risk.risk_score >= 15
                              ? '#f44336' // High risk → red
                              : risk.risk_score >= 8
                                ? '#ff9800' // Medium risk → orange
                                : '#4caf50', // Low risk → green
                          color: '#fff',
                        }}
                      >
                        Exp {risk.risk_score ?? '-'}
                      </StatusChip>
                    </Box>

                    <Typography sx={{ color: '#666', fontSize: '11px' }}>
                      Owner {risk.risk_owner || 'N/A'} • {risk.response_strategies || 'N/A'}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: '12px', color: '#999' }}>
                  No risks found for this plan
                </Typography>
              )}
            </Box>
          </MetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <MetricCard>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, borderBottom: '1px solid #E0E0E0' }}>
              <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500 }}>Constraints</Typography>
              <Button className="no-print" size='small' startIcon={<AddIcon />} sx={{ color: '#6200EE', textTransform: 'none', minHeight: '28px', padding: '2px 8px' }} onClick={() => setConstraintModalOpen(true)}>Add</Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {constraints.length > 0 ? (
                constraints.map((constraint) => (
                  <Box key={constraint.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: '#333' }}>
                        {constraint.name || 'Unnamed constraint'}
                      </Typography>

                      {/* ✅ Status-based color mapping */}
                      <StatusChip
                        color={
                          constraint.status === 'Compliance'
                            ? '#f44336' // Red
                            : constraint.status === 'Schedule'
                              ? '#FFA726' // Orange
                              : '#666666' // Gray
                        }
                      >
                        {constraint.status || 'N/A'}
                      </StatusChip>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: '12px', color: '#999' }}>
                  No constraints found for this plan
                </Typography>
              )}
            </Box>
          </MetricCard>
        </Grid>
      </Grid>

    </Box>)
  );
  const renderTasks = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1}}>
      <Grid container spacing={3} mb={2}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard>
            <MetricLabel>Tasks (All)</MetricLabel>
            <MetricValue>{jsonData.length}</MetricValue>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard>
            <MetricLabel>Late</MetricLabel>
            <MetricValue>{lateTaskCount}</MetricValue>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard>
            <MetricLabel>Critical Path</MetricLabel>
            <MetricValue>{criticalTaskCount}</MetricValue>
          </MetricCard>
        </Grid>
      </Grid>

      <MetricCard mb={2}>
        <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1, borderBottom: '1px solid #E0E0E0' }}>Late Tasks</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1, width: '80px' }}>ID</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Task</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Owner</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Start</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Finish</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
{lateTasks.length > 0 ? (
          lateTasks
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((task, index) => (
            <TableRow key={task.id} hover>
              <TableCell sx={{ color: '#6200EE', fontSize: '14px', py: 1 }}>{task.id}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>{task.text}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>{task.owner || "-"}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>{task.start_date?.slice(0, 10)}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>{task.end_date?.slice(0, 10)}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>  {task.summary ||
    (typeof task.parent === "string"
      ? task.parent.match(/name=([^,\]]+)/)?.[1] || "-"
      : "-")}</TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 2, color: '#888' }}>
              No late tasks
            </TableCell>
          </TableRow>
        )}
            </TableBody>
          </Table>
          <TablePagination
  component="div"
  count={lateTasks.length}
  page={page}
  onPageChange={handleChangePage}
  rowsPerPage={rowsPerPage}
  onRowsPerPageChange={handleChangeRowsPerPage}
  rowsPerPageOptions={[5, 10, 25]}
/>
        </TableContainer>
      </MetricCard>

      <MetricCard>
        <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1, borderBottom: '1px solid #E0E0E0' }}>Upcoming</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1, width: '80px' }}>ID</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Task</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Owner</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Start</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Finish</TableCell>
                <TableCell sx={{ color: '#666', fontSize: '14px', py: 1 }}>Summary</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
{upcomingTasks.length > 0 ? (
          upcomingTasks
          .slice(page1 * rowsPerPage1, page1 * rowsPerPage1 + rowsPerPage1)
          .map((task, index) => (
            <TableRow key={task.id} hover>
              <TableCell sx={{ color: '#6200EE', fontSize: '14px', py: 1 }}>{task.id}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>{task.text}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>{task.owner || "-"}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>{task.start_date?.slice(0, 10)}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>{task.end_date?.slice(0, 10)}</TableCell>
              <TableCell sx={{ fontSize: '14px', py: 1 }}>{task.summary || (task.parent?.match(/name=([^,\]]+)/)?.[1] || "-") || "-"}</TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 2, color: '#888' }}>
              No upcoming tasks
            </TableCell>
          </TableRow>
        )}
            </TableBody>
          </Table>
          <TablePagination
  component="div"
  count={upcomingTasks.length}
  page={page1}
  onPageChange={handleChangePage1}
  rowsPerPage={rowsPerPage1}
  onRowsPerPageChange={handleChangeRowsPerPage1}
  rowsPerPageOptions={[5, 10, 25]}
  />
  </TableContainer>
      </MetricCard>
    </Box>
  );

  const renderCost = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <MetricCard>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1, borderBottom: '1px solid #E0E0E0' }}>Cost Overview</Typography>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography sx={{ color: '#666', fontSize: '14px', mb: 1 }}>Baseline</Typography>
                <Typography sx={{ fontSize: '24px', fontWeight: 500 }}>₹{costData.baseline}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ color: '#666', fontSize: '14px', mb: 1 }}>Actual</Typography>
                <Typography sx={{ fontSize: '24px', fontWeight: 500 }}>₹{costData.actual}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ color: '#666', fontSize: '14px', mb: 1 }}>Remaining</Typography>
                <Typography sx={{ fontSize: '24px', fontWeight: 500 }}>₹{costData.baseline - costData.actual}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ color: '#666', fontSize: '14px', mb: 1 }}>Variance</Typography>
                <Typography sx={{ fontSize: '24px', fontWeight: 500 }}>₹{costData.actual - costData.baseline}</Typography>
              </Grid>
            </Grid>
          </MetricCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <MetricCard>
            <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1, borderBottom: '1px solid #E0E0E0' }}>Trend (mock)</Typography>
            <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: '#666' }}>(Mock) Cumulative cost line chart</Typography>
            </Box>
          </MetricCard>
        </Grid>
      </Grid>
    </Box>
  );

  const renderTeam = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard>
            <MetricLabel>People</MetricLabel>
            <MetricValue>{resources?.length || 0}</MetricValue>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard>
            <MetricLabel>Open Issues Owners</MetricLabel>
            <MetricValue>{assignedUserCount || 0}</MetricValue>
          </MetricCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard>
            <MetricLabel>Risk Owners</MetricLabel>
            <MetricValue>{uniqueRiskOwnerCount || 0}</MetricValue>
          </MetricCard>
        </Grid>
      </Grid>

      <MetricCard>
        <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1, borderBottom: '1px solid #E0E0E0' }}>Assignments</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    {resourceSummary?.length > 0 ? (
      resourceSummary.map((member, index) => (
        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{ fontSize: '14px', color: '#333', mb: 0.5 }}>
              {member.resource}
            </Typography>
          </Box>
          <Typography sx={{ color: '#6200EE', fontSize: '14px', pr: 2 }}>
            {member.count} tasks
          </Typography>
        </Box>
      ))
    ) : (
      <Typography sx={{ fontSize: '12px', color: '#999' }}>No resources assigned</Typography>
    )}
  </Box>
      </MetricCard>
    </Box>
  );
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [assumptionModalOpen, setAssumptionModalOpen] = useState(false);
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [dependencyModalOpen, setDependencyModalOpen] = useState(false);
  const [constraintModalOpen, setConstraintModalOpen] = useState(false);
    const [issueData, setIssueData] = useState({
    id: "",
    issue_type: "",
    who_raised_it: "",
    description: "",
    priority: "",
    assignedTo: "",
    targetResolutionDate: "",
    finalSolution: "",
    status: "Open"
  });

  const [assumptionData, setAssumptionData] = useState({
    id: "",
    category: "",
    dateLogged: new Date().toISOString().split('T')[0],
    assumption: "",
    confidence: "",
    owner: "",
    rationale: "",
    impact: "",
    status: "Open"
  });

  const [riskData, setRiskData] = useState({
    riskId: "",
    riskStatement: "",
    riskOwner: "",
    probability: "",
    impact: "",
    riskScore: "",
    responseStrategies: "",
    revisedProbability: "",
    revisedImpact: "",
    revisedScore: "",
    actions: "",
    status: "Open",
    comments: ""
  });
  const [dependencyData, setDependencyData] = useState({
    id: "",
    name: "",
    from: "",
    owner: "",
    dueDate: new Date().toISOString().split('T')[0],
    status: "Pending"
  });

  const [constraintData, setConstraintData] = useState({
    id: "",
    name: "",
    status: "Schedule"
  });
  const handleInputChange = (field, value) => {
    setIssueData(prev => ({ ...prev, [field]: value }));
  };
  const handleInputChange1 = (field, value) => {
    setAssumptionData(prev => ({ ...prev, [field]: value }));
  };
  const handleInputChange2 = (field, value) => {
    setRiskData(prev => ({ ...prev, [field]: value }));
  };
  const handleInputChange3 = (field, value) => {
    setDependencyData(prev => ({ ...prev, [field]: value }));
  };

  const handleInputChange4 = (field, value) => {
    setConstraintData(prev => ({ ...prev, [field]: value }));
  };

  const handleDashboardSave = async (data) => {
    try {
      const payload = {
        plan_id: planId,
        headerData: {
          "overall_complete": parseInt(overallProgress),
          "late_task": parseInt(lateTaskCount),
          "critical_path": parseInt(criticalTaskCount),
          "open_issues": parseInt(openIssueCount),
          "top_risk_exposure": topRiskScore.toString(),
          update_status: customColor,
        },
        milestonesData: milestoneTasks.map(m => ({
          name: m.name,
          owner: m.owner,
          planned: m.planned,
          actual: m.actual,
          variance: m.variance || 0,
        })),

        dashboardDetailsData: {
          complete_by_task: Object.fromEntries(
            (topSummaryTasks || []).map(t => [t.name, parseFloat(t.progress)])
          ),

          dependencies: (dependencies || []).map(dep => ({
            title: dep.name,
            source: dep.from,
            owner: dep.owner,
            due_date: dep.due_date,
            status: dep.status,
          })),

          issues: (issues || []).map(issue => ({
            title: issue.description,
            owner: issue.assignedTo || issue.who_raised_it || "",
            category: issue.issue_type || "General",
            due_date: issue.targetResolutionDate,
            priority: issue.priority,
          })),

          risk: (risks || []).map(r => ({
            title: r.risk_statement,
            owner: r.risk_owner,
            category: r.probability || "General",
            exposure: `Exp ${r.risk_score}`,
          })),

          constraints: (constraints || []).map(c => ({
            title: c.name,
            category: c.status,
          })),
        },
      };
      const response = await http.post('/master_service/create_dashboard/data', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success("Data updated successfully");
        setIsEditing(false);
      }
    } catch (error) {
      toast.error("Failed to save data");
    }
  };


  const handleIssueSave = async (data, closeModal, resetData) => {
    try {
      const payload = {
        issue_id: data.id,
        "description": data.description,
        "priority": data.priority,
        "issue_type": data.issue_type,
        "who_raised_it": data.who_raised_it,
        "plan_id": planId,
        "assigned_to": data.assignedTo,
        "target_resolution_date": data.targetResolutionDate,
        "status": data.status
      }
      const response = await http.post('/master_service/create_update/issues_data', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success("Data saved successfully");
        closeModal(false);
        resetData();
        fetchIssueData(planId);
      }
    } catch (error) {
      toast.error("Failed to save data");
    }
  };
  const handleAssumptionSave = async (data, closeModal, resetData) => {
    try {
      const payload = {
        ass_id: data.id,
        plan_id: planId,
        "category": data.category,
        "date_logged": data.dateLogged,
        "assumption": data.assumption,
        "confidence": data.confidence,
        "owner": data.owner,
        "rationale": data.rationale,
        "impact": data.impact,
        "status": data.status
      }
      const response = await http.post('/master_service/create_update/assumption', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success("Data saved successfully");
        closeModal(false);
        resetData();
        fetchAssumptionData(planId);
      }
    } catch (error) {
      toast.error("Failed to save data");
    }
  };
  const handleDependencySave = async (data, closeModal, resetData) => {
    try {
      const payload = {
        dept_id: data.id,
        plan_id: planId,
        "name": data.name,
        "from": data.from,
        "owner": data.owner,
        "due_date": data.due_date,
        "status": data.status
      }
     const response = await http.post('/master_service/create_update/dependency', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success("Data saved successfully");
        closeModal(false);
        resetData();
        fetchDependencyData(planId);
      }
    } catch (error) {
      toast.error("Failed to save data");
    }
  };
  const handleConstraintSave = async (data, closeModal, resetData) => {
    try {
      const payload = {
        const_id: data.id,
        "name": data.name,
        "plan_id": planId,
        "status": data.status
      }
      const response = await http.post('/master_service/create_update/constraint', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success("Data saved successfully");
        closeModal(false);
        resetData();
        fetchConstraintData(planId);
      }
    } catch (error) {
      toast.error("Failed to save data");
    }
  };
  const handleRiskSave = async (data, closeModal, resetData) => {
    try {
      const payload = {
        risk_id: data.id,
        risk_statement: data.risk_statement,
        risk_owner: data.risk_owner,
        probability: data.probability,
        impact: data.impact,
        risk_score: data.risk_score,
        revised_probability: data.revised_probability,
        revised_impact: data.revised_impact,
        revised_risk_score: data.revised_risk_score,
        action: data.action,
        response_strategies: data.response_strategies,
        comment: data.comment,
        plan_id: planId,
        "status": data.status
      }
      const response = await http.post('/master_service/create_update/risk_register', payload);
      if (response.status === 201 || response.status === 200) {
        toast.success("Data saved successfully");
        closeModal(false);
        resetData();
        fetchRiskData(planId);
      }
    } catch (error) {
      toast.error("Failed to save data");
    }
  };

  return (
    <Box id="dashboard-container" sx={{ p: 1, paddingTop: 0.1, backgroundColor: '#FFF' }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        backgroundColor: '#fff',
        borderRadius: '8px',
        pr: '5px',
        boxShadow: '2px 2px 2px 2px rgba(10, 5, 5, 0.1)'
      }}>
        {isSharedView() ? (
          <TextField
            value={planName || (planData.find(plan => String(plan.plan_id) === String(planId))?.label) || 'Loading...' }
            disabled
            sx={{
              minWidth: 200,
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#000',
                color: '#000',
                opacity: 1,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
              }
            }}
          />
        ) : (
          <StyledSelect
            value={selectedPlan}
            onChange={handlePlanChange}
            displayEmpty
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">
              Select a Plan
            </MenuItem>
            {planData.length > 0 ? (
              planData.map((plan) => (
                <MenuItem key={plan.plan_id} value={plan.value}>
                  {plan.label}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No plans available</MenuItem>
            )}
          </StyledSelect>
        )}
{isSharedView() ? (
  <TextField
    value={versionList.find(v => v.version === selectedVersion)?.version || 'Latest Version'}
    disabled
    sx={{
      minWidth: 180,
      '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: '#000',
        color: '#000',
        opacity: 1
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': { border: 'none' }
      }
    }}
  />
) : (
  <StyledSelect
    value={selectedVersion}
    onChange={handleVersionChange}
    displayEmpty
    renderValue={(selected) => (!selected ? "Select Version" : selected)}
    sx={{ minWidth: 180 }}
  >
    <MenuItem value="">Select Version</MenuItem>
    {versionList.map((v) => (
      <MenuItem key={v.version} value={v.version}>
        {`${v.version} (${new Date(v.createdAt).toLocaleString()})`}
      </MenuItem>
    ))}
  </StyledSelect>
)}

        {!isSharedView() && (<Box className="no-print" sx={{ display: 'flex', gap: 0.5, }}>
          <ActionButton 
            onClick={() => {
              if (!selectedPlan) {
                toast.warning("Please select a plan first");
                return;
              }
              handleExportPDF();
            }} 
            startIcon={<ImportIcon />} 
            sx={{ 
              textTransform: "none",
              opacity: selectedPlan ? 1 : 0.6,
              cursor: selectedPlan ? 'pointer' : 'not-allowed'
            }}
          >
            Export
          </ActionButton>
          <ActionButton 
            variant="contained" 
            startIcon={<ShareIcon />} 
            onClick={() => {
              if (!selectedPlan) {
                toast.warning("Please select a plan first");
                return;
              }
              handleShareModalOpen();
            }}
            sx={{ 
              textTransform: "none", 
              backgroundColor: '#FF2800', 
              color: '#fff', 
              '&:hover': { backgroundColor: '#FF2800' },
              opacity: selectedPlan ? 1 : 0.6,
              cursor: selectedPlan ? 'pointer' : 'not-allowed'
            }}
          >
            Share
          </ActionButton>
{isEditing ? (
              <ActionButton
                variant="contained"
                onClick={() => handleDashboardSave()}
                sx={{
                  textTransform: "none",
                  backgroundColor: '#FF2800',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#FF2800' }
                }}
              >
                Save
              </ActionButton>
            ) : (
              <ActionButton
                variant="contained"
                onClick={() => {
                  if (!selectedPlan) {
                    toast.warning("Please select a plan first");
                    return;
                  }
                  setIsEditing(true);
                }}
                sx={{
                  textTransform: "none",
                  backgroundColor: '#FF2800',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#FF2800' },
                  opacity: selectedPlan ? 1 : 0.6,
                  cursor: selectedPlan ? 'pointer' : 'not-allowed'
                }}
              >
                Edit
              </ActionButton>
            )}
          </Box>
  )}
  </Box>
            {/* Share Modal */}
            <ShareModal
              open={shareModalOpen}
              onClose={handleShareModalClose}
              aria-labelledby="share-modal-title"
            >
              <ShareModalContent sx={{ position: "relative" }}>
                <IconButton
                  onClick={handleShareModalClose}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 8,
                    color: "#999",
                    "&:hover": { color: "#FF2800", backgroundColor: "transparent" },
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6" id="share-modal-title" sx={{ mb: 0.5, mt: -2 }}>
                  Share Plan Dashboard
                </Typography>

                <RadioGroup 
                  row 
                  value={accessType} 
                  onChange={(e) => setAccessType(e.target.value)}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel
                    value="full-access"
                    control={
                      <Radio
                        sx={{
                          '&.Mui-checked': { color: '#FF2800' },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px' }}>
                        Full Access
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="limited-access"
                    control={
                      <Radio
                        sx={{
                          '&.Mui-checked': { color: '#FF2800' },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: '14px' }}>
                        Limited Access
                      </Typography>
                    }
                  />
                </RadioGroup>

                <InfoBox>
                  <InfoIcon sx={{ color: "#FFA500", mr: 1 }} />
                  <Typography variant="body2" sx={{ fontSize: '12px' }}>
                    <strong>Note:</strong> {accessType === 'full-access' 
                      ? 'Anyone with the link can access this plan dashboard. Please do not share confidential information.'
                      : 'Only invited members can access this plan dashboard. Please do not share confidential information.'}
                  </Typography>
                </InfoBox>

                {accessType === 'full-access' ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ mb: 1, fontWeight: 600 }}>Set Link Expiry</Typography>
                    <DateRangePicker
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                      allowFutureDates={true}
                      highlightColor='#FF2800'
                    />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        variant='outlined'
                        value={shareLink}
                        InputProps={{
                          readOnly: true,
                          sx: { height: '30px' }
                        }}
                      />
                      <Button
                        variant='contained'
                        onClick={() => {
                          handleGenerateLink();
                          handleCopyLink();
                        }}
                        sx={{
                          ml: 1,
                          backgroundColor: '#FF2800',
                          '&:hover': { backgroundColor: '#FF2800' },
                          height: '30px',
                          textTransform: 'none',
                          width: '120px',
                          whiteSpace: 'nowrap',
                          fontSize: '12px'
                        }}
                      >
                        {isCopied ? 'Copied!' : 'Copy Link'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mt: 3 }}>
                    <Autocomplete
                      multiple
                      fullWidth
                      freeSolo
                      options={emailOptions}
                      value={selectedEmails}
                      getOptionLabel={(option) => option.label || ''}
                      onChange={(event, newValue) => {
                        setSelectedEmails(
                          newValue.map((item) =>
                            typeof item === "string" ? { label: item, value: item } : item
                          )
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          placeholder="Invite members by mail"
                          error={!!errors.email}
                          helperText={errors.email}
                          sx={{ mb: 2 }}
                        />
                      )}
                    />

                    <TextField
                      fullWidth
                      label="Subject"
                      variant="outlined"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      error={!!errors.subject}
                      helperText={errors.subject}
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      fullWidth
                      label="Message"
                      variant="outlined"
                      multiline
                      rows={4}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      error={!!errors.message}
                      helperText={errors.message}
                      sx={{ mb: 3 }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleShareModalClose}
                        sx={{textTransform: 'none'}}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSendInvite}
                        sx={{ backgroundColor: '#FF2800', textTransform: 'none', '&:hover': { backgroundColor: '#FF2800' } }}
                      >
                        Send Invite
                      </Button>
                    </Box>
                  </Box>
                )}
              </ShareModalContent>
            </ShareModal>
          

      <StyledTabs value={selectedTab} onChange={handleTabChange}>
        <StyledTab label="Project Overview" mb={1} />
        {!isSharedView() && <StyledTab className="no-print" label="Tasks" />}
        {!isSharedView() && <StyledTab className="no-print" label="Cost" />}
        {!isSharedView() && <StyledTab className="no-print" label="Team" />}
      </StyledTabs>


      <Box sx={{ mt: 1 }}>
        {selectedTab === 0 && renderProjectOverview()}
        {selectedTab === 1 && !isSharedView() && renderTasks()}
        {selectedTab === 2 && !isSharedView() && renderCost()}
        {selectedTab === 3 && !isSharedView() && renderTeam()}
      </Box>
      {/* Issue Modal */}
      <Modal
        open={issueModalOpen}
        onClose={() => setIssueModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end"
        }}
      >
        <Box
          sx={{
            width: "350px",
            height: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 1,
            overflowY: "auto",
            '& .MuiInputBase-input': { // Add this style to reduce input font size
              fontSize: '0.875rem'
            },
            '& .MuiInputLabel-root': { // Add this style to reduce label font size
              fontSize: '0.875rem'
            },
            '& .MuiMenuItem-root': { // Add this style to reduce dropdown item font size
              fontSize: '0.875rem'
            }
          }}
        >
          <Typography mb={1.5} style={{ font: 'bold', fontWeight: '600' }}>Add Issue</Typography>

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel>Issue Type</InputLabel>
            <Select
              value={issueData.issue_type || "Others"}
              label="Issue Type"
              onChange={(e) => handleInputChange("issue_type", e.target.value)}
              size="small"
              renderValue={(selected) => selected || "Others"}
            >
              <MenuItem value="Technical">Technical</MenuItem>
              <MenuItem value="Requirement">Requirement</MenuItem>
              <MenuItem value="Resource">Resource</MenuItem>
              <MenuItem value="Others">Others</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="raised-from" shrink={true}>Raised From</InputLabel>
            <Select
              value={issueData.who_raised_it || ""}
              labelId="raised-from"
              label="Raised From"
              onChange={(e) => handleInputChange("who_raised_it", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="Dev Team">Dev Team</MenuItem>
              <MenuItem value="Product Owner">Product Owner</MenuItem>
              <MenuItem value="PM">PM</MenuItem>
              <MenuItem value="QA Engineer">QA Engineer</MenuItem>
              <MenuItem value="Stakeholder">Stakeholder</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={issueData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 200 }}
          />

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="priority" shrink={true}>Priority</InputLabel>
            <Select
              value={issueData.priority}
              labelId="priority"
              label="Priority"
              onChange={(e) => handleInputChange("priority", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel>Assigned To</InputLabel>
            <Select
              value={issueData.assignedTo || ""}
              label="Assigned To"
              onChange={(e) => handleInputChange("assignedTo", e.target.value)}
              size="small"
              renderValue={(selected) => {
                if (!selected) return "Select User"; // ✅ Placeholder
                const selectedUser = resources.find((r) => r.value === selected);
                return selectedUser ? selectedUser.label : selected; // ✅ Show name instead of email
              }}
            >
              {resources.map((resource) => (
                <MenuItem key={resource.label} value={resource.value}>
                  {resource.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="date"
            label="Target Resolution Date"
            value={issueData.targetResolutionDate}
            onChange={(e) => handleInputChange("targetResolutionDate", e.target.value)}
            sx={{ mb: 1.5 }}
            InputLabelProps={{
              shrink: true, // keeps label visible above the date field
            }}
            inputProps={{
              min: new Date().toISOString().split("T")[0]  // ✅ Only allow today & future
            }}
            size="small"
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Final Solution"
            value={issueData.final_solution}
            onChange={(e) => handleInputChange("final_solution", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <Button
            fullWidth
            onClick={() => handleIssueSave(issueData, setIssueModalOpen, () => setIssueData({
              id: "",
              issue_type: "Others",
              who_raised_it: "",
              description: "",
              priority: "",
              assignedTo: "",
              targetResolutionDate: "",
              finalSolution: "",
              status: "Open"
            }))}
            sx={{
              textTransform: "none", bgcolor: '#FF2800', color: 'white',
              "&:hover": {
                bgcolor: "#FF2800", // keep same color on hover
              },
            }}
          >
            Save
          </Button>

          <Button
            fullWidth
            onClick={() => setIssueModalOpen(false)}
            sx={{ textTransform: "none", bgcolor: 'white', color: 'black' }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>

      {/* Assumption Modal */}
      <Modal
        open={assumptionModalOpen}
        onClose={() => setAssumptionModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end"
        }}
      >
        <Box
          sx={{
            width: "350px",
            height: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 1,
            overflowY: "auto",
            '& .MuiInputBase-input': { // Add this style to reduce input font size
              fontSize: '0.875rem'
            },
            '& .MuiInputLabel-root': { // Add this style to reduce label font size
              fontSize: '0.875rem'
            },
            '& .MuiMenuItem-root': { // Add this style to reduce dropdown item font size
              fontSize: '0.875rem'
            }
          }}
        >
          <Typography mb={1} style={{ font: 'bold', fontWeight: '600' }}>Add Assumption</Typography>
          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              value={assumptionData.category}
              label="Category"
              onChange={(e) =>
                handleInputChange1("category", e.target.value)
              }
            >
              <MenuItem value="Strategic">Strategic</MenuItem>
              <MenuItem value="Operational">Operational</MenuItem>
              <MenuItem value="Financial">Financial</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="date"
            label="Date Logged"
            value={assumptionData.dateLogged}
            onChange={(e) => handleInputChange1("dateLogged", e.target.value)}
            sx={{ mb: 1.5 }}
            InputLabelProps={{
              shrink: true, // keeps label visible above the date field
            }}
            size="small"
            inputProps={{
              max: new Date().toISOString().split("T")[0]  // ✅ Only allow today & future
            }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Assumption"
            value={assumptionData.assumption}
            onChange={(e) => handleInputChange1("assumption", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 200 }}
          />

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="confidence-label">Confidence</InputLabel>
            <Select
              labelId="confidence-label"
              value={assumptionData.confidence}
              label="Confidence"
              onChange={(e) => handleInputChange1("confidence", e.target.value)}
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Owner"
            value={assumptionData.owner}
            onChange={(e) => {
              const newValue = e.target.value;
              handleInputChange1("owner", newValue);
            }
            }
            sx={{ mb: 1.5 }}
            size="small"
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Rationale"
            value={assumptionData.rationale}
            onChange={(e) => handleInputChange1("rationale", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 100 }}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Impact"
            value={assumptionData.impact}
            onChange={(e) => handleInputChange1("impact", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <FormControl fullWidth sx={{ mb: 0.5 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={assumptionData.status}
              label="Status"
              onChange={(e) => handleInputChange1("status", e.target.value)}
              size="small"
            >
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <Button
            fullWidth
            onClick={() => handleAssumptionSave(assumptionData, setAssumptionModalOpen, () => setAssumptionData({
              id: "",
              category: "",
              dateLogged: new Date().toISOString().split('T')[0],
              assumption: "",
              confidence: "",
              owner: "",
              rationale: "",
              impact: "",
              status: "Open"
            }))}
            sx={{
              textTransform: "none",
              bgcolor: '#FF2800',
              color: 'white',
              mb: 0.5,
              "&:hover": { bgcolor: "#FF2800" }
            }}
          >
            Save
          </Button>

          <Button
            fullWidth
            onClick={() => setAssumptionModalOpen(false)}
            sx={{ textTransform: "none", bgcolor: 'white', color: 'black' }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>

      {/* Risk Modal */}
      <Modal
        open={riskModalOpen}
        onClose={() => setRiskModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end"
        }}
      >
        <Box
          sx={{
            width: "350px",
            height: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 1,
            overflowY: "auto",
            '& .MuiInputBase-input': { // Add this style to reduce input font size
              fontSize: '0.875rem'
            },
            '& .MuiInputLabel-root': { // Add this style to reduce label font size
              fontSize: '0.875rem'
            },
            '& .MuiMenuItem-root': { // Add this style to reduce dropdown item font size
              fontSize: '0.875rem'
            }
          }}
        >
          <Typography mb={1.5} style={{ font: 'bold', fontWeight: '600' }}>Add Risk</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Risk Statement"
            value={riskData.risk_statement}
            onChange={(e) => handleInputChange2("risk_statement", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 200 }}
          />

          <TextField
            fullWidth
            label="Risk Owner"
            value={riskData.risk_owner}
            onChange={(e) => {
              const newValue = e.target.value;
              handleInputChange2("risk_owner", newValue);
            }}
            sx={{ mb: 1.5 }}
            size="small"
          />

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="probability-label" shrink="true">Probability</InputLabel>
            <Select
              value={riskData.probability}
              labelId="probability-label"
              label="Probability"
              onChange={(e) => handleInputChange2("probability", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="impact-label" shrink="true">Impact</InputLabel>
            <Select
              value={riskData.impact}
              labelId="impact-label"
              label="Impact"
              onChange={(e) => handleInputChange2("impact", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Very High">Very High</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Risk Score"
            value={riskData.risk_score}
            onChange={(e) => {
              const newValue = e.target.value.replace(/[^0-9]/g, "");
              handleInputChange2("risk_score", newValue)
            }}
            sx={{ mb: 1.5 }}
            size="small"
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Response Strategies"
            value={riskData.response_strategies}
            onChange={(e) => handleInputChange2("response_strategies", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 200 }}
          />

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="revised-probability" shrink="true">Revised Probability</InputLabel>
            <Select
              value={riskData.revised_probability}
              labelId="revised-probability"
              label="Revised Probability"
              onChange={(e) => handleInputChange2("revised_probability", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="revised-impact" shrink="true">Revised Impact</InputLabel>
            <Select
              value={riskData.revised_impact}
              labelId="revised-impact"
              label="Revised Impact"
              onChange={(e) => handleInputChange2("revised_impact", e.target.value)}
              size="small"
              notched
            >
              <MenuItem value="High">High</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Low">Low</MenuItem>
              <MenuItem value="Very High">Very High</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Revised Score"
            value={riskData.revised_risk_score}
            onChange={(e) => {
              const newValue = e.target.value.replace(/[^0-9]/g, "");
              handleInputChange2("revised_risk_score", newValue);
            }}
            sx={{ mb: 1.5 }}
            size="small"
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Actions"
            value={riskData.action}
            onChange={(e) => handleInputChange2("action", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <FormControl fullWidth sx={{ mb: 1.5 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={riskData.status}
              label="Status"
              onChange={(e) => handleInputChange2("status", e.target.value)}
              size="small"
            >
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="In-Progress">In-Progress</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="comment"
            value={riskData.comment}
            onChange={(e) => handleInputChange2("comment", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <Button
            fullWidth
            onClick={() => handleRiskSave(riskData, setRiskModalOpen, () => setRiskData({
              id: "",
              risk_statement: "",
              risk_owner: "",
              probability: "",
              impact: "",
              risk_score: "",
              response_strategies: "",
              revised_probability: "",
              revised_impact: "",
              revised_risk_score: ""
            }))}
            sx={{
              textTransform: "none",
              bgcolor: '#FF2800',
              color: 'white',
              mb: 0.5,
              "&:hover": { bgcolor: "#FF2800" }
            }}
          >
            Save
          </Button>

          <Button
            fullWidth
            onClick={() => setRiskModalOpen(false)}
            sx={{ textTransform: "none", bgcolor: 'white', color: 'black' }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
      <Modal
        open={dependencyModalOpen}
        onClose={() => setDependencyModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end"
        }}
      >
        <Box
          sx={{
            width: "350px",
            height: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 1,
            overflowY: "auto",
            '& .MuiInputBase-input': { // Add this style to reduce input font size
              fontSize: '0.875rem'
            },
            '& .MuiInputLabel-root': { // Add this style to reduce label font size
              fontSize: '0.875rem'
            },
            '& .MuiMenuItem-root': { // Add this style to reduce dropdown item font size
              fontSize: '0.875rem'
            }
          }}
        >
          <Typography mb={1.5} style={{ font: 'bold', fontWeight: '600' }}>Add Dependency</Typography>

          <TextField
            fullWidth
            label="Name"
            value={dependencyData.name}
            onChange={(e) => {
              const newValue = e.target.value;
              handleInputChange3("name", newValue);
            }}
            sx={{ mb: 1.5 }}
            size="small"
          />

          <TextField
            fullWidth
            label="From"
            value={dependencyData.from}
            onChange={(e) => handleInputChange3("from", e.target.value)}
            sx={{ mb: 1.5 }}
            size="small"
          />

          <TextField
            fullWidth
            label="Owner"
            value={dependencyData.owner}
            onChange={(e) => {
              const newValue = e.target.value;
              handleInputChange3("owner", newValue);
            }}
            sx={{ mb: 1.5 }}
            size="small"
          />

          <TextField
            fullWidth
            type="date"
            label="Due Date"
            value={dependencyData.due_date}
            onChange={(e) => handleInputChange3("due_date", e.target.value)}
            sx={{ mb: 1.5 }}
            InputLabelProps={{
              shrink: true,
            }}
            size="small"
            inputProps={{
              min: new Date().toISOString().split("T")[0]  // ✅ Only allow today & future
            }}
          />

          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="dependency-status-label" shrink={true}>Status</InputLabel>
            <Select
              labelId="dependency-status-label"
              value={dependencyData.status}
              label="Status"
              onChange={(e) => handleInputChange3("status", e.target.value)}
              notched
            >
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Requested">Requested</MenuItem>
              <MenuItem value="Confirmed">Confirmed</MenuItem>
            </Select>
          </FormControl>
          <Button
            fullWidth
            onClick={() => handleDependencySave(dependencyData, setDependencyModalOpen, () => setDependencyData({
              id: "",
              name: "",
              from: "",
              owner: "",
              due_date: new Date().toISOString().split('T')[0],
              status: "Pending"
            }))}
            sx={{
              textTransform: "none",
              bgcolor: '#FF2800',
              color: 'white',
              mb: 0.5,
              "&:hover": { bgcolor: "#FF2800" }
            }}
          >
            Save
          </Button>

          <Button
            fullWidth
            onClick={() => setDependencyModalOpen(false)}
            sx={{ textTransform: "none", bgcolor: 'white', color: 'black' }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>

      {/* Add Constraint Modal */}
      <Modal
        open={constraintModalOpen}
        onClose={() => setConstraintModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end"
        }}
      >
        <Box
          sx={{
            width: "350px",
            height: "100%",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 1,
            overflowY: "auto",
            '& .MuiInputBase-input': { // Add this style to reduce input font size
              fontSize: '0.875rem'
            },
            '& .MuiInputLabel-root': { // Add this style to reduce label font size
              fontSize: '0.875rem'
            },
            '& .MuiMenuItem-root': { // Add this style to reduce dropdown item font size
              fontSize: '0.875rem'
            }
          }}
        >
          <Typography mb={1.5} style={{ font: 'bold', fontWeight: '600' }}>Add Constraint</Typography>

          <TextField
            fullWidth
            label="Name"
            value={constraintData.name}
            onChange={(e) => {
              const newValue = e.target.value;
              handleInputChange4("name", newValue);
            }}
            sx={{ mb: 1.5 }}
            size="small"
          />
          <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
            <InputLabel id="constraint-status-label" shrink={true}>Status</InputLabel>
            <Select
              labelId="constraint-status-label"
              value={constraintData.status}
              label="Status"
              onChange={(e) => handleInputChange4("status", e.target.value)}
              notched
            >
              <MenuItem value="Schedule">Schedule</MenuItem>
              <MenuItem value="Technical">Technical</MenuItem>
              <MenuItem value="Compliance">Compliance</MenuItem>
            </Select>
          </FormControl>
          <Button
            fullWidth
            onClick={() => handleConstraintSave(constraintData, setConstraintModalOpen, () => setConstraintData({
              id: "",
              type: "",
              description: "",
              impact: "",
              mitigation: "",
              status: "Active"
            }))}
            sx={{
              textTransform: "none",
              bgcolor: '#FF2800',
              color: 'white',
              mb: 0.5,
              "&:hover": { bgcolor: "#FF2800" }
            }}
          >
            Save
          </Button>

          <Button
            fullWidth
            onClick={() => setConstraintModalOpen(false)}
            sx={{ textTransform: "none", bgcolor: 'white', color: 'black' }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
    </Box>
  );
}